import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import i18n from 'i18n';
import { isListEnabled } from 'utils/queryHelpers';

import { readingUnitService } from 'services';
import {
  BaseReadingUnitModel,
  NotificationType,
  QueryContextFromKeys,
  QueryParamModel,
  ReadingUnitModel,
  ReadingUnitSummaryModel,
} from 'models';
import { ClosingDateChangeFormProps } from 'pages/Leo/ReadingUnits/ClosingDateChangeDialog';
import { TransferTaskFormProps } from 'pages/Leo/ReadingUnits/TransferTaskDialog';
import { isAxiosError } from 'utils/errors';
import { Status } from 'utils/request';
import { showNotification } from 'components';
import { arrayify } from 'utils/arrays';

type ReadingUnitQueryContexts = QueryContextFromKeys<typeof readingUnitsKeys>;

enum Errors {
  ASSIGN = 'THERE_IS_NO_WORKER_ASSIGNED_TO_THE_READING_UNIT',
}

export const readingUnitsKeys = {
  all: [{ scope: 'readingUnits' }] as const,
  lists: () => [{ ...readingUnitsKeys.all[0], entity: 'list' }] as const,
  list: (params?: QueryParamModel | null) => [{ ...readingUnitsKeys.lists()[0], params }] as const,
  orderList: (id: number, params?: QueryParamModel | null) =>
    [{ ...readingUnitsKeys.lists()[0], id, params }] as const,
  order: (id: ID) => [{ ...readingUnitsKeys.lists()[0], id }] as const,
  readingFailureReasons: (ids: ID[]) => [{ ...readingUnitsKeys.lists()[0], ids }] as const,
  orderDetails: (id: string, orderId?: string) =>
    [{ ...readingUnitsKeys.lists()[0], id, orderId }] as const,
  // Deliberately not derived from lists(): an export must not land on a list query's cache entry.
  exportList: (params?: QueryParamModel | null) =>
    [{ ...readingUnitsKeys.all[0], entity: 'export', params }] as const,
  exportDetails: (id: number, params?: QueryParamModel | null) =>
    [{ ...readingUnitsKeys.all[0], entity: 'export', id, params }] as const,
  orderDelete: (id: string[]) => [{ ...readingUnitsKeys.lists()[0], id }] as const,
};

export function hasClosingDatePassed(row?: BaseReadingUnitModel | BaseReadingUnitModel[]) {
  const currentDate = new Date();
  currentDate.setHours(0, 0, 0, 0);

  return arrayify(row).some((item) => {
    const inputDate = new Date(item.currentClosingDate);
    inputDate.setHours(0, 0, 0, 0);

    return inputDate < currentDate;
  });
}

export function hasBeenRevokedBySap(row: ReadingUnitSummaryModel | ReadingUnitSummaryModel[]) {
  return arrayify(row).some((item) => item.orderCount === item.revokedOrderCount);
}

export enum TransferErrorType {
  ESTIMATION_DATE_PASSED = 'ESTIMATION_DATE_PASSED',
  REVOKED_BY_SAP = 'REVOKED_BY_SAP',
}

export function isTransferableReadingUnit(
  data: ReadingUnitSummaryModel | ReadingUnitSummaryModel[]
) {
  if (hasClosingDatePassed(data)) {
    return TransferErrorType.ESTIMATION_DATE_PASSED;
  }

  if (hasBeenRevokedBySap(data)) {
    return TransferErrorType.REVOKED_BY_SAP;
  }

  return true;
}

const fetchReadingUnitList = async ({ queryKey: [{ params }] }: ReadingUnitQueryContexts['list']) =>
  readingUnitService.getReadingUnitList(params);

const fetchReadingUnit = async ({ queryKey: [{ id }] }: ReadingUnitQueryContexts['order']) =>
  readingUnitService.getReadingUnit(id);

const fetchReadingFailureReasons = async ({
  queryKey: [{ ids }],
}: ReadingUnitQueryContexts['readingFailureReasons']) =>
  readingUnitService.getReadingUnitReadingFailureReasons(ids);

const fetchReadingUnitOrderDetails = async ({
  queryKey: [{ id, orderId }],
}: ReadingUnitQueryContexts['orderDetails']) =>
  readingUnitService.getReadingUnitOrderDetails(id, orderId);

const exportReadingUnits = async ({
  queryKey: [{ params }],
}: ReadingUnitQueryContexts['exportList']) => readingUnitService.exportReadingUnits(params);

const fetchReadingUnitOrderList = async ({
  queryKey: [{ id, params }],
}: ReadingUnitQueryContexts['orderList']) => readingUnitService.getReadingUnitOrderList(id, params);

const exportReadingUnitOrders = async ({
  queryKey: [{ id, params }],
}: ReadingUnitQueryContexts['exportDetails']) =>
  readingUnitService.exportReadingUnitOrders(id, params);

export const useSaveTransferTask = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ selected, data }: { selected: number[]; data: TransferTaskFormProps }) =>
      readingUnitService.saveTransferTask(selected, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: readingUnitsKeys.lists() });
    },
  });
};

export const useSaveTransferConsumptionPlace = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      selected,
      data,
      readingUnitId: _readingUnitId,
    }: {
      selected: number[];
      readingUnitId: string;
      data: TransferTaskFormProps;
    }) => readingUnitService.saveTransferConsumptionPlace(selected, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: readingUnitsKeys.lists() });
    },
  });
};

export const useSaveClosingDate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ selected, data }: { selected: number[]; data: ClosingDateChangeFormProps }) =>
      readingUnitService.saveClosingDate(selected, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: readingUnitsKeys.lists() });
    },
  });
};

export const useEditReadingUnit = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: ID; data: Partial<ReadingUnitModel> }) =>
      readingUnitService.editReadingUnit(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: readingUnitsKeys.lists() });
    },
    onError: (e) => {
      if (
        isAxiosError(e) &&
        e.response?.status === 400 &&
        e.response.data.message === Errors.ASSIGN
      ) {
        showNotification({
          content: i18n.t(`ERRORS.${Errors.ASSIGN}`),
          type: NotificationType.ERROR,
        });
      }
    },
  });
};

export const useReadingUnitReadingFailureReasons = (ids: ID[]) => {
  return useQuery({
    queryKey: readingUnitsKeys.readingFailureReasons(ids),
    queryFn: fetchReadingFailureReasons,
    enabled: !!ids.length,
  });
};

export const useDeleteReadingUnitOrder = (closeDialog: () => void) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string[]) => readingUnitService.deleteReadingUnit(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: readingUnitsKeys.lists() });
    },
    onError: (e) => {
      if (isAxiosError(e) && e.response?.status === Status.FORBIDDEN) {
        showNotification({
          content: i18n.t(`ERRORS.NOT_RIGHT`),
          type: NotificationType.ERROR,
        });
      }
      closeDialog();
    },
  });
};

export const useReadingUnitList = (params?: QueryParamModel | null) => {
  return useQuery({
    queryKey: readingUnitsKeys.list(params),
    queryFn: fetchReadingUnitList,
    enabled: isListEnabled(params),
  });
};

export const useReadingUnit = (id: ID) => {
  return useQuery({ queryKey: readingUnitsKeys.order(id), queryFn: fetchReadingUnit });
};

export const useReadingUnitWorkers = (id: number, enabled: boolean) => {
  return useQuery({
    queryKey: ['reading-unit-workers', id],
    queryFn: () => readingUnitService.getReadingUnitWorkers(id),
    enabled,
    gcTime: 300000,
    staleTime: 300000,
  });
};

export const useExportReadingUnits = (params?: QueryParamModel | null) => {
  return useQuery({
    queryKey: readingUnitsKeys.exportList(params),
    queryFn: exportReadingUnits,
    enabled: false,
    // The download is a side effect, never rendered - keeping the entry cached only pins memory.
    gcTime: 0,
  });
};

export const useReadingUnitOrderList = (id: number, params?: QueryParamModel | null) => {
  return useQuery({
    queryKey: readingUnitsKeys.orderList(id, params),
    queryFn: fetchReadingUnitOrderList,
  });
};

export const useExportReadingUnitOrders = (id: number, params?: QueryParamModel | null) => {
  return useQuery({
    queryKey: readingUnitsKeys.exportDetails(id, params),
    queryFn: exportReadingUnitOrders,
    enabled: false,
    // The download is a side effect, never rendered - keeping the entry cached only pins memory.
    gcTime: 0,
  });
};

export const useReadingUnitOrderDetails = (id: string, orderId?: string) => {
  return useQuery({
    queryKey: readingUnitsKeys.orderDetails(id, orderId),
    queryFn: fetchReadingUnitOrderDetails,
  });
};

export const useReadingUnitCacheMetadata = () => {
  return useQuery({
    queryKey: ['reading-unit-cache-metadata'],
    queryFn: () => readingUnitService.getCacheMetadata(),
    refetchInterval: () => 10_000,
  });
};
